import React, { useRef } from 'react';
import QRCode from 'react-qr-code';
import { toast } from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { motion } from 'framer-motion';
import { widgetStyles, animations } from '../../utils/designTokens';

const QRCodeCard = ({ 
  value, 
  fg = "#000000", 
  bg = "#FFFFFF", 
  size = 256, 
  level = "M", 
  logo = null,
  logoSize = 60,
  frameText = "Scan to Book Your Appointment",
  poweredByText = "Powered by Hairchitech",
  shopName = "",
  showFrame = true
}) => {
  const { isDark } = useTheme();
  const qrRef = useRef(null);


  const downloadPNG = () => {
    try {
      // Get the entire QR code container (including frame elements)
      const qrContainer = qrRef.current;
      if (!qrContainer) {
        toast.error('QR code not found');
        return;
      }

      // Use html2canvas to capture the entire container with all elements
      import('html2canvas').then(({ default: html2canvas }) => {
        html2canvas(qrContainer, {
          backgroundColor: isDark ? '#1f2937' : '#ffffff',
          scale: 2, // Higher quality
          useCORS: true,
          allowTaint: true,
          width: qrContainer.offsetWidth,
          height: qrContainer.offsetHeight
        }).then(canvas => {
          // Create download link
          const url = canvas.toDataURL('image/png');
          const a = document.createElement('a');
          a.href = url;
          a.download = `qr-code-${Date.now()}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          toast.success('PNG downloaded successfully!');
        }).catch(error => {
          console.error('Error generating PNG:', error);
          toast.error('Failed to generate PNG');
        });
      }).catch(() => {
        // Fallback: try to install html2canvas
        toast.error('html2canvas library not available. Please refresh the page and try again.');
      });
    } catch (error) {
      console.error('Error downloading PNG:', error);
      toast.error('Failed to download PNG');
    }
  };

  const downloadSVG = () => {
    try {
      const qrContainer = qrRef.current;
      if (!qrContainer) {
        toast.error('QR code not found');
        return;
      }

      // Calculate total dimensions including frame elements
      const qrSize = size;
      const framePadding = showFrame ? 48 : 16; // Extra space for frame elements
      const totalWidth = qrSize + (framePadding * 2);
      const totalHeight = qrSize + (framePadding * 2) + (showFrame ? 120 : 0); // Extra height for text

      // Create a comprehensive SVG that includes all elements
      let svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${totalHeight}" viewBox="0 0 ${totalWidth} ${totalHeight}">`;
      
      // Add background
      svgContent += `<rect width="100%" height="100%" fill="${bg}"/>`;
      
      // Add frame text if enabled
      if (showFrame && frameText) {
        const textY = 24;
        svgContent += `<text x="${totalWidth/2}" y="${textY}" text-anchor="middle" font-family="Arial, sans-serif" font-size="16" font-weight="bold" fill="${isDark ? '#ffffff' : '#000000'}">${frameText}</text>`;
      }
      
      // Add QR code
      const qrX = framePadding;
      const qrY = showFrame ? framePadding + 32 : framePadding;
      
      // Get the original QR code SVG
      const originalSvg = qrContainer.querySelector('svg');
      if (originalSvg) {
        let qrSvgContent = new XMLSerializer().serializeToString(originalSvg);
        
        // Add logo to QR code if present
        if (logo) {
          let logoDataUrl = logo;
          if (!logo.startsWith('data:')) {
            logoDataUrl = logo;
          }
          
          const logoX = (qrSize - logoSize) / 2;
          const logoY = (qrSize - logoSize) / 2;
          const logoElement = `<image href="${logoDataUrl}" x="${logoX}" y="${logoY}" width="${logoSize}" height="${logoSize}" />`;
          qrSvgContent = qrSvgContent.replace('</svg>', `${logoElement}</svg>`);
        }
        
        // Extract the inner content of the QR SVG (without the outer svg tag)
        const innerContent = qrSvgContent.replace(/^<svg[^>]*>/, '').replace(/<\/svg>$/, '');
        
        // Add the QR code as a group with proper positioning
        svgContent += `<g transform="translate(${qrX}, ${qrY})">${innerContent}</g>`;
      }
      
      // Add shop name if enabled
      if (showFrame && shopName) {
        const shopNameY = qrY + qrSize + 20;
        svgContent += `<text x="${totalWidth/2}" y="${shopNameY}" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" font-weight="medium" fill="${isDark ? '#d1d5db' : '#374151'}">${shopName}</text>`;
      }
      
      // Add powered by text if enabled
      if (showFrame && poweredByText) {
        const poweredByY = qrY + qrSize + (shopName ? 40 : 20);
        svgContent += `<text x="${totalWidth/2}" y="${poweredByY}" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" fill="${isDark ? '#9ca3af' : '#6b7280'}">${poweredByText}</text>`;
      }
      
      svgContent += '</svg>';

      const svgBlob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      const a = document.createElement('a');
      a.href = svgUrl;
      a.download = `qr-code-${Date.now()}.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(svgUrl);
      toast.success('SVG downloaded successfully!');
    } catch (error) {
      console.error('Error downloading SVG:', error);
      toast.error('Failed to download SVG');
    }
  };

  // Share via Email
  const shareViaEmail = () => {
    const subject = encodeURIComponent('Book Your Appointment');
    const body = encodeURIComponent(`${frameText}\n\nScan the QR code or visit: ${value}`);
    const emailUrl = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = emailUrl;
    toast.success('Opening email client...');
  };

  return (
    <motion.div 
      className={`p-6 rounded-lg cursor-pointer ${widgetStyles.base} ${widgetStyles.hover3D} ${widgetStyles.brandAccent}`}
      initial={animations.initial}
      whileHover={animations.hover}
      whileTap={animations.tap}
    >
      <div className="text-center">
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>QR Code Preview</h3>
        
        {/* QR Code Display */}
        <div ref={qrRef} className="flex justify-center mb-4">
          <div className={`${showFrame ? 'p-6' : 'p-4'} relative`}>
            {/* Frame Text */}
            {showFrame && frameText && (
              <div className="text-center mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {frameText}
                </h3>
              </div>
            )}
            
            {/* QR Code */}
            <div 
              className="rounded-lg relative mx-auto"
              style={{ 
                backgroundColor: bg,
                width: `${size}px`,
                height: `${size}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <QRCode
                value={value}
                fgColor={fg}
                bgColor={bg}
                size={size}
                level={level}
              />
              {logo && (
                <div 
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                  style={{
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: `${logoSize}px`,
                    height: `${logoSize}px`,
                    backgroundColor: bg,
                    borderRadius: '8px',
                    padding: '4px'
                  }}
                >
                  <img
                    src={logo}
                    alt="Logo"
                    className="w-full h-full object-contain rounded"
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                  />
                </div>
              )}
            </div>
            
            {/* Shop Name */}
            {showFrame && shopName && (
              <div className="text-center mt-4">
                <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {shopName}
                </p>
              </div>
            )}
            
            {/* Powered By */}
            {showFrame && poweredByText && (
              <div className="text-center mt-2">
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {poweredByText}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Download Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              onClick={downloadPNG}
              className={`px-4 py-2 rounded-md transition-colors text-sm font-medium ${
                isDark 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
            >
              Download PNG
            </button>
            <button
              onClick={downloadSVG}
              className={`px-4 py-2 rounded-md transition-colors text-sm font-medium ${
                isDark 
                  ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                  : 'bg-purple-500 hover:bg-purple-600 text-white'
              }`}
            >
              Download SVG
            </button>
          </div>
          
          {/* Share Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              onClick={shareViaEmail}
              className={`px-4 py-2 rounded-md transition-colors text-sm font-medium ${
                isDark 
                  ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                  : 'bg-gray-500 hover:bg-gray-600 text-white'
              }`}
            >
              Share via Email
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default QRCodeCard;
